import React, { useReducer, useCallback, useState, startTransition } from 'react';
import { motion } from 'framer-motion';
import { GameState, GameConfig, Position } from './types/game';
import { gameReducer, hasRemainingWords, hasPotentialMoves } from './utils/gameLogic';
import { validateWord } from './utils/wordValidator';
import GameGrid from './components/GameGrid';
import GameUI from './components/GameUI';
import DebugPanel from './components/DebugPanel';
import LevelSelect from './components/LevelSelect';
import LevelComplete from './components/LevelComplete';
import GridDebugView from './components/GridDebugView';
import BrandSpinner from './components/BrandSpinner';
import AdSlot from './components/AdSlot';
import PrivacyModal from './components/PrivacyModal';
import CookieConsent from './components/CookieConsent';
import Leaderboard from './components/Leaderboard';
import { submitScore } from './utils/leaderboard';
import { IS_DEBUG } from './utils/env';
import { initGridWorker, preGenerateLevel, cleanupWorker } from './utils/gridWorkerManager';
import { performanceMonitor } from './utils/performanceMonitor';
import './App.css';

const initialConfig: GameConfig = {
  gridSize: 4,
  difficulty: 'easy'
};

const initialGameState: GameState = {
  grid: [],
  selectedPath: [],
  score: 0,
  foundWords: [],
  gameComplete: false,
  currentWord: '',
  feedbackMessage: '',
  level: 1,
  allWordsFound: false
};

const STORAGE_KEY = 'letterweave_state_v1';

function loadInitialState(): GameState {
  if (typeof window === 'undefined') return initialGameState;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return initialGameState;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && parsed.state) {
      const state = parsed.state as Partial<GameState>;
      // Basic shape validation; fall back to defaults if invalid
      if (Array.isArray(state.grid) && Array.isArray(state.foundWords)) {
        return {
          ...initialGameState,
          ...state
        } as GameState;
      }
    }
  } catch {}
  return initialGameState;
}

function App() {
  const [gameState, dispatch] = useReducer(gameReducer, initialGameState, loadInitialState);
  const [showDebug, setShowDebug] = useState(false);
  const [showGridDebug, setShowGridDebug] = useState(false);
  const [isGravityActive, setIsGravityActive] = useState(false);
  const [isShrinking, setIsShrinking] = useState(false);
  const [showLevelSelect, setShowLevelSelect] = useState(false);
  const [showLevelComplete, setShowLevelComplete] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [completedLevel, setCompletedLevel] = useState<number>(1);
  const [completionBonus, setCompletionBonus] = useState(0);
  const [previousGrid, setPreviousGrid] = useState<GameState['grid']>([]);
  const [previousSelectedPath, setPreviousSelectedPath] = useState<Position[]>([]);
  const [showConfirmNewGame, setShowConfirmNewGame] = useState(false);
  const [isGridLoading, setIsGridLoading] = useState(true);
  const [isSubmittingWord, setIsSubmittingWord] = useState(false);
  const [isValidatingWord, setIsValidatingWord] = useState(false);
  const [wordValidationResult, setWordValidationResult] = useState<'valid' | 'invalid' | null>(null);
  const [suppressCompletion, setSuppressCompletion] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [preGeneratedGrid, setPreGeneratedGrid] = useState<{ level: number; grid: GameState['grid'] } | null>(null);
  
  // Track if we're in the middle of a word submission to prevent completion checks from running
  const isSubmittingRef = React.useRef(false);
  // Track if we've already submitted the score for this level to prevent duplicates
  const scoreSubmittedRef = React.useRef<{ level: number; score: number } | null>(null);
  // Track periodic check interval for no-moves detection
  const noMovesCheckIntervalRef = React.useRef<NodeJS.Timeout | null>(null);

  // Helper function to check for no moves and trigger completion if needed
  const checkAndTriggerCompletionIfNoMoves = useCallback(() => {
    // Skip if already complete, animating, submitting, or no words found yet
    if (showLevelComplete || 
        isGravityActive || 
        isSubmittingRef.current || 
        suppressCompletion ||
        gameState.grid.length === 0 || 
        gameState.foundWords.length === 0 ||
        gameState.feedbackMessage.startsWith('Level Complete!')) {
      return false;
    }

    // Quick heuristic check first - if not enough letters, no moves possible
    if (!hasPotentialMoves(gameState.grid)) {
      // No potential moves - trigger completion
      const baseBonus = Math.max(100, gameState.foundWords.length * 10);
      const totalBonus = baseBonus + (gameState.tileClearingBonus || 0) + 
                        (gameState.allWordsFound ? gameState.foundWords.length * 25 : 0);
      setCompletionBonus(totalBonus);
      setCompletedLevel(gameState.level);
      
      // Submit score to leaderboard (only once per level completion)
      if (!scoreSubmittedRef.current || 
          scoreSubmittedRef.current.level !== gameState.level ||
          scoreSubmittedRef.current.score !== gameState.score) {
        scoreSubmittedRef.current = { level: gameState.level, score: gameState.score };
        submitScore(
          gameState.score,
          gameState.level,
          gameState.foundWords.length
        ).catch(error => {
          console.warn('Failed to submit score to leaderboard:', error);
        });
      }
      
      setShowLevelComplete(true);
      return true;
    }

    // If heuristic passes, do full check (but defer it to avoid blocking)
    // This will be handled by the existing completion checks
    return false;
  }, [showLevelComplete, isGravityActive, suppressCompletion, gameState, setCompletionBonus, setCompletedLevel, setShowLevelComplete]);

  const handleLetterClick = useCallback((position: { row: number; col: number }) => {
    // Quick check before processing click - if no moves, trigger completion
    if (!hasPotentialMoves(gameState.grid)) {
      checkAndTriggerCompletionIfNoMoves();
      return; // Don't process the click if level is complete
    }
    
    dispatch({ type: 'SELECT_LETTER', position });
  }, [dispatch, gameState.grid, checkAndTriggerCompletionIfNoMoves]);

  const handleSubmitWord = useCallback(() => {
    // CRITICAL: Set flag immediately to prevent completion checks from running
    isSubmittingRef.current = true;
    
    // Start performance monitoring during animations
    if (IS_DEBUG) {
      performanceMonitor.startMonitoring();
      // Stop monitoring after animations complete
      setTimeout(() => {
        performanceMonitor.stopMonitoring();
      }, 4000); // Stop after ~4 seconds (allows for full animation sequence)
    }
    
    // Step 1: Press Submit Word - show red tint immediately
    setIsSubmittingWord(true);
    setIsValidatingWord(true);
    setWordValidationResult(null);
    
    // Capture selected path immediately (cheap operation)
    const prevSelectedPath = [...gameState.selectedPath];
    setPreviousSelectedPath(prevSelectedPath);
    
    // Validate word (quick check)
    const word = gameState.currentWord;
    const isValid = word.length >= 3 && validateWord(word);
    
    // Validate immediately - red tint shows while validation happens
    if (!isValid) {
      // Invalid word - smooth fade back to normal
      setIsValidatingWord(false);
      setWordValidationResult('invalid');
      setIsSubmittingWord(false);
      // Clear after fade animation
      setTimeout(() => {
        setWordValidationResult(null);
      }, 500); // Fade duration
      // Dispatch to clear selection and show error
      dispatch({ type: 'SUBMIT_WORD' });
      return;
    }
    
    // Valid word - start explosion sequence IMMEDIATELY
    setIsValidatingWord(false);
    setWordValidationResult(null); // Clear red tint - explosion will start
    
    // CRITICAL: Capture grid BEFORE any state changes (cheap operation)
    // Do this synchronously but it's fast - just array references
    const gridBeforeSubmit = gameState.grid.map(row => [...row]);
    
    // CRITICAL: Start animations IMMEDIATELY - don't defer with startTransition
    // Animations need to start synchronously to prevent visual glitches
    // The time-sliced calculation will handle the heavy work without blocking
    setIsGravityActive(true);
    setPreviousGrid(gridBeforeSubmit);
    
    // CRITICAL: Defer heavy reducer work to avoid blocking animations
    // Use requestAnimationFrame to defer dispatch until after animations start
    requestAnimationFrame(() => {
      // Use React 18's startTransition to mark reducer as non-urgent
      // This allows React to yield to animations if needed
      startTransition(() => {
        // Dispatch heavy reducer operation (non-urgent)
        // This will update the grid state, but animations are already started
        dispatch({ type: 'SUBMIT_WORD' });
        setIsSubmittingWord(false);
      });
    });
    
    // Step 4: Start falling during explosion to create seamless overlap
    // Explosion is 2.5s, start falling at 1.2s (1.3s overlap) for smooth transition
    // This allows explosions to continue while falling starts
    setTimeout(() => {
      // Start falling during explosion - both animations will run simultaneously
      setIsShrinking(true);
      
      // Complete cleanup after ALL animations complete
      // Explosion: 2.5s, Falling starts at 1.2s, so falling ends at 1.2s + 0.7s = 1.9s
      // But we need to wait for explosion to complete (2.5s) + buffer
      setTimeout(() => {
        setIsShrinking(false);
        // Clear removingTiles BEFORE clearing isGravityActive to prevent incorrect removals
        setTimeout(() => {
          // Clear previousGrid first to prevent any grid switching issues
          setPreviousGrid([]);
          setPreviousSelectedPath([]);
          // Then clear gravity - this will trigger cleanup in GameGrid
          setIsGravityActive(false);
          // Clear submission flag to allow completion checks again
          isSubmittingRef.current = false;
        }, 100); // Buffer to ensure removingTiles are cleared first
      }, 1400); // Wait for explosion to complete (2.5s - 1.2s = 1.3s) + falling duration (0.7s) + buffer
    }, 1200); // Start falling at 1.2s (1.3s before 2.5s explosion ends) for seamless overlap
  }, [gameState.grid, gameState.selectedPath, gameState.currentWord, dispatch]);

  const handleClearSelection = useCallback(() => {
    dispatch({ type: 'CLEAR_SELECTION' });
  }, []);

  const doStartNewGame = useCallback(() => {
    // Show spinner, then defer dispatch so the UI can paint before heavy work
    setIsGridLoading(true);
    setTimeout(() => {
      dispatch({ type: 'NEW_GAME', config: initialConfig });
    }, 0);
  }, []);

  const handleNewGame = useCallback(() => {
    setShowConfirmNewGame(true);
  }, []);

  const handleNextLevel = useCallback(() => {
    setIsGridLoading(true);
    // Temporarily suppress completion overlay during level transition
    setSuppressCompletion(true);
    // Reset score submission ref for new level
    scoreSubmittedRef.current = null;
    
    // Check if we have a pre-generated grid ready
    const nextLevel = gameState.level + 1;
    const usePreGenerated = preGeneratedGrid && preGeneratedGrid.level === nextLevel;
    
    if (usePreGenerated) {
      // Pre-generated grid is ready - start loading immediately, show grid quickly
      // Dispatch on next frame to allow modal to start closing
      setTimeout(() => {
        dispatch({ 
          type: 'NEXT_LEVEL', 
          config: initialConfig,
          preGeneratedGrid: preGeneratedGrid!.grid
        });
        // Clear used pre-generated grid
        setPreGeneratedGrid(null);
        // Clear loading immediately since grid is pre-generated
        setIsGridLoading(false);
        setTimeout(() => setSuppressCompletion(false), 100);
      }, 0); // Remove delay - grid is ready, dispatch immediately
    } else {
      // Generate synchronously - keep loading state until grid is ready
      // Use setTimeout to allow React to batch state updates and show loading UI
        setTimeout(() => {
          dispatch({ 
            type: 'NEXT_LEVEL', 
            config: initialConfig,
            preGeneratedGrid: undefined
          });
          // Loading clears automatically via useEffect when grid is set
          setTimeout(() => setSuppressCompletion(false), 150);
        }, 0);
    }
  }, [gameState.level, preGeneratedGrid]);

  const handleLevelSelect = useCallback((levelId: number) => {
    dispatch({ type: 'NEW_GAME', config: { ...initialConfig, level: levelId } });
    setShowLevelSelect(false);
  }, []);
  
  // Debug handler: Change grid size (only in debug mode)
  const handleGridSizeChange = useCallback((gridSize: number) => {
    // Determine a level that uses this grid size
    // Grid size increases every 3 levels: 3x3 (1-3), 4x4 (4-6), 5x5 (7-9), 6x6 (10+)
    // For debug, use the first level that matches the size
    let levelForSize = 1;
    if (gridSize === 3) levelForSize = 1; // Levels 1-3 use 3x3
    else if (gridSize === 4) levelForSize = 4; // Levels 4-6 use 4x4
    else if (gridSize === 5) levelForSize = 7; // Levels 7-9 use 5x5
    else if (gridSize === 6) levelForSize = 10; // Levels 10+ use 6x6 (max)
    
    setIsGridLoading(true);
    dispatch({ 
      type: 'NEW_GAME', 
      config: { gridSize, difficulty: 'medium', level: levelForSize } 
    });
  }, []);

  const handleNextLevelFromComplete = useCallback(() => {
    setShowLevelComplete(false);
    handleNextLevel();
  }, [handleNextLevel]);

  const handleReplayLevel = useCallback(() => {
    setShowLevelComplete(false);
    setIsGridLoading(true);
    // Suppress completion detection during replay to prevent false completion
    setSuppressCompletion(true);
    // Clear localStorage to ensure we start fresh
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch {}
    }
    // Mark that we've checked so the on-mount completion check doesn't run
    hasCheckedOnMount.current = true;
    // Restart the completed level - use stored level to ensure we replay the correct one
    const levelToReplay = completedLevel;
    setTimeout(() => {
      dispatch({ 
        type: 'NEW_GAME', 
        config: { gridSize: initialConfig.gridSize, difficulty: initialConfig.difficulty, level: levelToReplay } 
      });
      // Re-enable completion detection after a short delay to allow state to update
      setTimeout(() => {
        setSuppressCompletion(false);
      }, 300);
    }, 0);
  }, [completedLevel]);

  const handleLevelSelectFromComplete = useCallback(() => {
    setShowLevelComplete(false);
    setShowLevelSelect(true);
  }, []);

  const toggleDebug = useCallback(() => {
    setShowDebug(prev => !prev);
  }, []);

  const toggleGridDebug = useCallback(() => {
    setShowGridDebug(prev => !prev);
  }, []);

  // Initialize worker on mount
  React.useEffect(() => {
    initGridWorker();
    return () => {
      cleanupWorker();
    };
  }, []);

  // Pre-generate next level in background when current level starts
  React.useEffect(() => {
    if (gameState.grid.length > 0 && gameState.level > 0) {
      const nextLevel = gameState.level + 1;
      
      // Skip if we already have this level cached
      if (preGeneratedGrid && preGeneratedGrid.level === nextLevel) {
        return;
      }
      
      // Clear old pre-generated grid if it's for a different level
      if (preGeneratedGrid && preGeneratedGrid.level !== nextLevel) {
        setPreGeneratedGrid(null);
      }
      
      // Defer pre-generation slightly to avoid blocking UI during level transition
      // This ensures animations and state updates complete before starting background work
      setTimeout(() => {
        // Start background generation
        preGenerateLevel(nextLevel, (grid) => {
          if (grid) {
            setPreGeneratedGrid({ level: nextLevel, grid });
          }
        });
      }, 50); // Small delay to let UI updates settle
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState.level]);

  // Track if we've checked on mount to avoid re-checking
  const hasCheckedOnMount = React.useRef(false);

  // Initialize game on first load if nothing was restored
  React.useEffect(() => {
    if (gameState.grid.length === 0) {
      doStartNewGame();
      hasCheckedOnMount.current = true;
      return;
    }

    // On mount/load, check if level should be complete (safety check)
    // This runs once when component mounts and gameState is restored
    if (!hasCheckedOnMount.current && gameState.grid.length > 0) {
      hasCheckedOnMount.current = true;
      
      // Wait for UI to stabilize before checking
      setTimeout(() => {
        const hasCompletionMessage = gameState.feedbackMessage.startsWith('Level Complete!');
        const isGridEmpty = gameState.grid.length < 2;
        const hasGameCompleteFlag = gameState.gameComplete;
        
        // If no clear completion indicators, actually check for remaining words
        let shouldBeComplete = hasCompletionMessage || isGridEmpty || hasGameCompleteFlag;
        
        if (!shouldBeComplete && gameState.foundWords.length > 0 && gameState.grid.length > 0) {
          // Actually check if there are remaining words - but only if grid is small enough
          // CRITICAL: Defer this expensive check to avoid blocking UI during level load
          // OPTIMIZE: Calculate without .flat() - just multiply rows * cols
          const gridRows = gameState.grid.length;
          const gridCols = gameState.grid[0]?.length || 0;
          const remainingTilesCount = gridRows * gridCols;
          if (remainingTilesCount <= 9) {
            // Defer expensive check to avoid blocking level load
            // Use a long delay to ensure it runs after animations and UI updates complete
            setTimeout(() => {
              try {
                const hasRemaining = hasRemainingWords(gameState.grid, gameState.foundWords);
                shouldBeComplete = !hasRemaining;
              } catch (e) {
                console.error('[Level Check] Error checking remaining words:', e);
              }
            }, 3000); // Long delay to ensure it doesn't block anything
          }
        }

        if (shouldBeComplete && !showLevelComplete && gameState.foundWords.length > 0) {
          const baseBonus = Math.max(100, gameState.foundWords.length * 10);
          const totalBonus = baseBonus + (gameState.tileClearingBonus || 0) + 
                            (gameState.allWordsFound ? gameState.foundWords.length * 25 : 0);
          setCompletionBonus(totalBonus);
          setCompletedLevel(gameState.level);
          
          // Submit score to leaderboard (only once per level completion)
          if (!scoreSubmittedRef.current || 
              scoreSubmittedRef.current.level !== gameState.level ||
              scoreSubmittedRef.current.score !== gameState.score) {
            scoreSubmittedRef.current = { level: gameState.level, score: gameState.score };
            submitScore(
              gameState.score,
              gameState.level,
              gameState.foundWords.length
            ).catch(error => {
              console.warn('Failed to submit score to leaderboard:', error);
            });
          }
          
          setTimeout(() => {
            setShowLevelComplete(true);
          }, 200);
        }
      }, 500); // Give UI time to stabilize
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState.grid.length, gameState.foundWords, doStartNewGame]);

  // Persist game state to localStorage on changes
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const stateToPersist: Partial<GameState> = {
        grid: gameState.grid,
        selectedPath: gameState.selectedPath,
        score: gameState.score,
        foundWords: gameState.foundWords,
        gameComplete: gameState.gameComplete,
        currentWord: gameState.currentWord,
        feedbackMessage: '', // don't persist transient feedback text
        level: gameState.level,
        allWordsFound: gameState.allWordsFound,
        tileClearingBonus: gameState.tileClearingBonus
      };
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ version: 1, state: stateToPersist })
      );
    } catch {}
  }, [gameState]);

  // When grid updates with content, clear loading state
  React.useEffect(() => {
    if (gameState.grid.length > 0) {
      setIsGridLoading(false);
    }
  }, [gameState.grid]);


  // Detect level completion (based on feedback message to avoid false triggers on NEXT_LEVEL)
  React.useEffect(() => {
    const isCompletionMessage = gameState.feedbackMessage.startsWith('Level Complete!');
    if (isCompletionMessage && !showLevelComplete && !suppressCompletion) {
      const baseBonus = Math.max(100, gameState.foundWords.length * 10);
      const totalBonus = baseBonus + (gameState.tileClearingBonus || 0) + 
                        (gameState.allWordsFound ? gameState.foundWords.length * 25 : 0);
      setCompletionBonus(totalBonus);
      setCompletedLevel(gameState.level);
      
      // Submit score to leaderboard (only once per level completion)
      if (!scoreSubmittedRef.current || 
          scoreSubmittedRef.current.level !== gameState.level ||
          scoreSubmittedRef.current.score !== gameState.score) {
        scoreSubmittedRef.current = { level: gameState.level, score: gameState.score };
        submitScore(
          gameState.score,
          gameState.level,
          gameState.foundWords.length
        ).catch(error => {
          console.warn('Failed to submit score to leaderboard:', error);
        });
      }
      
      // Use a small delay to ensure animations have started showing, but don't wait for them to complete
      setTimeout(() => {
        setShowLevelComplete(true);
      }, 100);
    }
  }, [gameState.feedbackMessage, showLevelComplete, gameState.foundWords.length, suppressCompletion, gameState.tileClearingBonus, gameState.allWordsFound, gameState.level, gameState.score]);

  // Background completion check - start when animations begin, run in parallel
  // This runs in parallel with animations, so results are ready when animations finish
  const completionCheckRef = React.useRef<{ hasRemaining: boolean | null; isChecking: boolean; gridVersion: number }>({ hasRemaining: null, isChecking: false, gridVersion: 0 });
  const gridVersionRef = React.useRef(0);
  
  React.useEffect(() => {
    // Start background completion check when animations begin (isGravityActive becomes true)
    // CRITICAL: Defer this check significantly to avoid interfering with explosion animation
    // Wait until after explosion has fully started (2.5s duration) before starting check
    if (isGravityActive && !completionCheckRef.current.isChecking) {
      const gridRows = gameState.grid.length;
      const gridCols = gameState.grid[0]?.length || 0;
      const remainingTilesCount = gridRows * gridCols;
      const shouldCheck = (remainingTilesCount <= 36 || gameState.foundWords.length >= 4);
      
      if (shouldCheck && gameState.foundWords.length > 0 && 
          !gameState.feedbackMessage.startsWith('Level Complete!') && 
          !showLevelComplete) {
        
        // Track this grid version to avoid duplicate checks
        gridVersionRef.current += 1;
        const currentGridVersion = gridVersionRef.current;
        completionCheckRef.current.isChecking = true;
        completionCheckRef.current.gridVersion = currentGridVersion;
        
        // CRITICAL: Defer the check significantly to avoid interfering with explosion animation
        // Wait 2.5 seconds (explosion duration) before starting the check
        // This ensures the explosion animation runs smoothly without any blocking
        setTimeout(() => {
          // Only process if this is still the current grid version
          if (completionCheckRef.current.gridVersion !== currentGridVersion) {
            completionCheckRef.current.isChecking = false;
            return; // Grid changed, this check is outdated
          }
          
          // Use requestIdleCallback if available for non-blocking execution
          const scheduleCheck = (callback: () => void) => {
            if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
              (window as any).requestIdleCallback(callback, { timeout: 200 });
            } else {
              // Use setTimeout to ensure it runs during idle time
              setTimeout(callback, 0);
            }
          };
          
          scheduleCheck(() => {
            // Only process if this is still the current grid version
            if (completionCheckRef.current.gridVersion !== currentGridVersion) {
              completionCheckRef.current.isChecking = false;
              return; // Grid changed, this check is outdated
            }
            
            try {
              // Check the current grid state (after word submission)
              const hasRemaining = hasRemainingWords(gameState.grid, gameState.foundWords);
              completionCheckRef.current.hasRemaining = hasRemaining;
              completionCheckRef.current.isChecking = false;
              
              // If no remaining words, wait for animations to complete before showing modal
              if (!hasRemaining) {
                // Submit score to leaderboard (only once per level completion)
                if (!scoreSubmittedRef.current || 
                    scoreSubmittedRef.current.level !== gameState.level ||
                    scoreSubmittedRef.current.score !== gameState.score) {
                  scoreSubmittedRef.current = { level: gameState.level, score: gameState.score };
                  submitScore(
                    gameState.score,
                    gameState.level,
                    gameState.foundWords.length
                  ).catch(error => {
                    console.warn('Failed to submit score to leaderboard:', error);
                  });
                }
                // Don't trigger yet - wait for animations to finish
                // The backup check below will use this result
              }
            } catch (e) {
              console.error('[Completion Backup] Error checking:', e);
              completionCheckRef.current.isChecking = false;
            }
          });
        }, 2500); // Wait for explosion animation to complete (2.5s) before starting check
      }
    } else if (!isGravityActive && !isSubmittingRef.current) {
      // Reset when animations complete and we're not submitting
      completionCheckRef.current = { hasRemaining: null, isChecking: false, gridVersion: 0 };
    }
  }, [isGravityActive, gameState.grid, gameState.foundWords, gameState.feedbackMessage, showLevelComplete, gameState.level, gameState.score]);

  // Backup completion detection - check when animations complete if background check didn't trigger
  React.useEffect(() => {
    // Skip if suppressing completion, already showing modal, no grid, or if we haven't found any words yet (fresh start)
    // CRITICAL: Also skip if animations are active OR if we're currently submitting a word
    // MUST check this FIRST before any expensive operations
    if (suppressCompletion || showLevelComplete || gameState.grid.length === 0 || gameState.foundWords.length === 0 || isGravityActive || isSubmittingRef.current) {
      return;
    }

    // OPTIMIZE: Calculate remainingTilesCount without .flat() - just multiply rows * cols
    // This avoids the expensive .flat() operation which can be slow on large grids
    const gridRows = gameState.grid.length;
    const gridCols = gameState.grid[0]?.length || 0;
    const remainingTilesCount = gridRows * gridCols;
    const shouldCheck = (remainingTilesCount <= 36 || gameState.foundWords.length >= 4);
    if (shouldCheck && gameState.foundWords.length > 0 && !gameState.feedbackMessage.startsWith('Level Complete!')) {
      // If background check already completed, use its result
      if (completionCheckRef.current.hasRemaining === false) {
        const baseBonus = Math.max(100, gameState.foundWords.length * 10);
        const totalBonus = baseBonus + (gameState.tileClearingBonus || 0) + 
                          (gameState.allWordsFound ? gameState.foundWords.length * 25 : 0);
        setCompletionBonus(totalBonus);
        setCompletedLevel(gameState.level);
        setShowLevelComplete(true);
        return;
      }
      
      // If background check is still running or hasn't started, do a quick check now
      // This is a fallback if background check didn't complete in time
      const timeoutId = setTimeout(() => {
        // Skip if background check is still running - it will handle completion
        if (completionCheckRef.current.isChecking) {
          return;
        }
        
        try {
          // Quick heuristic check first
          if (!hasPotentialMoves(gameState.grid)) {
            // No potential moves - trigger completion
            const baseBonus = Math.max(100, gameState.foundWords.length * 10);
            const totalBonus = baseBonus + (gameState.tileClearingBonus || 0) + 
                              (gameState.allWordsFound ? gameState.foundWords.length * 25 : 0);
            setCompletionBonus(totalBonus);
            setCompletedLevel(gameState.level);
            
            // Submit score to leaderboard (only once per level completion)
            if (!scoreSubmittedRef.current || 
                scoreSubmittedRef.current.level !== gameState.level ||
                scoreSubmittedRef.current.score !== gameState.score) {
              scoreSubmittedRef.current = { level: gameState.level, score: gameState.score };
              submitScore(
                gameState.score,
                gameState.level,
                gameState.foundWords.length
              ).catch(error => {
                console.warn('Failed to submit score to leaderboard:', error);
              });
            }
            
            setShowLevelComplete(true);
            return;
          }

          // Full check if heuristic passes
          const hasRemaining = hasRemainingWords(gameState.grid, gameState.foundWords);
          if (!hasRemaining) {
            // All words found but completion not triggered - trigger it now
            const baseBonus = Math.max(100, gameState.foundWords.length * 10);
            const totalBonus = baseBonus + (gameState.tileClearingBonus || 0) + 
                              (gameState.allWordsFound ? gameState.foundWords.length * 25 : 0);
            setCompletionBonus(totalBonus);
            setCompletedLevel(gameState.level);
            
            // Submit score to leaderboard (only once per level completion)
            if (!scoreSubmittedRef.current || 
                scoreSubmittedRef.current.level !== gameState.level ||
                scoreSubmittedRef.current.score !== gameState.score) {
              scoreSubmittedRef.current = { level: gameState.level, score: gameState.score };
              submitScore(
                gameState.score,
                gameState.level,
                gameState.foundWords.length
              ).catch(error => {
                console.warn('Failed to submit score to leaderboard:', error);
              });
            }
            
            setShowLevelComplete(true);
          }
        } catch (e) {
          console.error('[Completion Backup] Error checking:', e);
        }
      }, 100); // Very short delay - just to ensure animations have started
      
      return () => clearTimeout(timeoutId);
    }
  }, [gameState.grid, gameState.foundWords, showLevelComplete, suppressCompletion, gameState.feedbackMessage, gameState.tileClearingBonus, gameState.allWordsFound, gameState.level, gameState.score, isGravityActive]);

  // Periodic check for no-moves scenario (every 4 seconds when idle)
  // This catches cases where user is stuck with no valid moves
  React.useEffect(() => {
    // Clear any existing interval
    if (noMovesCheckIntervalRef.current) {
      clearInterval(noMovesCheckIntervalRef.current);
      noMovesCheckIntervalRef.current = null;
    }

    // Only run periodic check if:
    // - Not already complete
    // - Not animating
    // - Not submitting
    // - Have found words (game has started)
    // - Grid exists
    if (!showLevelComplete && 
        !isGravityActive && 
        !isSubmittingRef.current && 
        !suppressCompletion &&
        gameState.grid.length > 0 && 
        gameState.foundWords.length > 0 &&
        !gameState.feedbackMessage.startsWith('Level Complete!')) {
      
      // Set up periodic check every 4 seconds
      noMovesCheckIntervalRef.current = setInterval(() => {
        // Double-check conditions before running expensive check
        if (showLevelComplete || 
            isGravityActive || 
            isSubmittingRef.current || 
            suppressCompletion ||
            gameState.grid.length === 0 || 
            gameState.foundWords.length === 0) {
          return;
        }

        // Quick heuristic check first
        if (!hasPotentialMoves(gameState.grid)) {
          checkAndTriggerCompletionIfNoMoves();
          return;
        }

        // If heuristic passes, do full check (defer to avoid blocking)
        // Use requestIdleCallback for non-blocking execution
        const scheduleCheck = (callback: () => void) => {
          if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
            (window as any).requestIdleCallback(callback, { timeout: 500 });
          } else {
            setTimeout(callback, 0);
          }
        };

        scheduleCheck(() => {
          // Re-check conditions after scheduling
          if (showLevelComplete || 
              isGravityActive || 
              isSubmittingRef.current || 
              suppressCompletion ||
              gameState.grid.length === 0 || 
              gameState.foundWords.length === 0) {
            return;
          }

          try {
            const hasRemaining = hasRemainingWords(gameState.grid, gameState.foundWords);
            if (!hasRemaining) {
              // No remaining words - trigger completion
              const baseBonus = Math.max(100, gameState.foundWords.length * 10);
              const totalBonus = baseBonus + (gameState.tileClearingBonus || 0) + 
                                (gameState.allWordsFound ? gameState.foundWords.length * 25 : 0);
              setCompletionBonus(totalBonus);
              setCompletedLevel(gameState.level);
              
              // Submit score to leaderboard (only once per level completion)
              if (!scoreSubmittedRef.current || 
                  scoreSubmittedRef.current.level !== gameState.level ||
                  scoreSubmittedRef.current.score !== gameState.score) {
                scoreSubmittedRef.current = { level: gameState.level, score: gameState.score };
                submitScore(
                  gameState.score,
                  gameState.level,
                  gameState.foundWords.length
                ).catch(error => {
                  console.warn('Failed to submit score to leaderboard:', error);
                });
              }
              
              setShowLevelComplete(true);
            }
          } catch (e) {
            console.error('[Periodic No-Moves Check] Error:', e);
          }
        });
      }, 4000); // Check every 4 seconds
    }

    // Cleanup interval on unmount or when conditions change
    return () => {
      if (noMovesCheckIntervalRef.current) {
        clearInterval(noMovesCheckIntervalRef.current);
        noMovesCheckIntervalRef.current = null;
      }
    };
  }, [showLevelComplete, isGravityActive, suppressCompletion, gameState, checkAndTriggerCompletionIfNoMoves, setCompletionBonus, setCompletedLevel, setShowLevelComplete]);

  return (
    <div className="App">
      <motion.div 
        className="game-container"
        initial={{ opacity: isGridLoading ? 1 : 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: isGridLoading ? 0 : 0.2 }}
      >
        <div className="main-content">
          {/* Compact Header */}
          <GameUI
            gameState={gameState}
            onSubmitWord={handleSubmitWord}
            onClearSelection={handleClearSelection}
            onRequestNewGame={handleNewGame}
            onNextLevel={handleNextLevel}
            onShowLeaderboard={() => setShowLeaderboard(true)}
          />
          
          {/* Entry Box - Controls and Word Display Above Grid */}
          <div className="entry-box-above-grid">
            {/* Action Buttons */}
            <div className="action-buttons-bar">
              <motion.button
                className="action-button submit-button"
                onClick={handleSubmitWord}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={gameState.selectedPath.length < 3 || isSubmittingWord}
              >
                {isSubmittingWord ? 'Processing...' : 'Submit Word'}
              </motion.button>
              <motion.button
                className="action-button hint-button"
                onClick={() => {
                  dispatch({ type: 'REQUEST_HINT_PROGRESSIVE' });
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={!!gameState.hintSession && gameState.hintSession.revealedCount >= gameState.hintSession.path.length}
              >
                Hint
              </motion.button>
              <motion.button
                className="action-button clear-button"
                onClick={handleClearSelection}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Clear
              </motion.button>
            </div>
            
            {/* Current Word Display */}
            <div className="current-word-section">
              <motion.div 
                className="current-word"
                animate={{ 
                  scale: gameState.currentWord ? 1.05 : 1,
                  color: gameState.currentWord ? '#4CAF50' : '#999'
                }}
                transition={{ duration: 0.2 }}
              >
                {gameState.currentWord || 'Select letters to form a word...'}
              </motion.div>
              {gameState.feedbackMessage && (() => {
                const isSuccess = gameState.feedbackMessage.startsWith('Great!') || gameState.feedbackMessage.startsWith('Level Complete!');
                const isHintMsg = gameState.feedbackMessage.startsWith('Hint:');
                const messageClass = `feedback-message ${isHintMsg ? 'hint' : isSuccess ? 'success' : 'error'}`;
                return (
                  <motion.div 
                    className={messageClass}
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.2 }}
                  >
                    {gameState.feedbackMessage}
                  </motion.div>
                );
              })()}
            </div>
          </div>

          {/* Game Grid - Center Focus */}
          <div className="grid-area">
            {gameState.grid.length > 0 && (
              <GameGrid
                gameState={gameState}
                onLetterClick={handleLetterClick}
                isGravityActive={isGravityActive}
                isShrinking={isShrinking}
                previousGrid={previousGrid}
                selectedPathForAnimation={isGravityActive ? previousSelectedPath : []}
              />
            )}
            {/* Only show spinner for initial grid loading, not during word submission animations */}
            {isGridLoading && (
              <div className="grid-loading-overlay">
                <BrandSpinner />
              </div>
            )}
          </div>

          {/* Found Words - Collapsible */}
          {/* Handled in GameUI component */}
          
          {/* Ad Below */}
          <div className="ad-container ad-below-grid">
            <AdSlot />
          </div>
        </div>
        
        {IS_DEBUG && showDebug && (
          <DebugPanel
            grid={gameState.grid}
            showDebug={showDebug}
            onToggleDebug={toggleDebug}
            showGridDebug={showGridDebug}
            onToggleGridDebug={toggleGridDebug}
            onGridSizeChange={IS_DEBUG ? handleGridSizeChange : undefined}
          />
        )}
        <div className="page-footer-links">
          <button className="cookie-link" onClick={() => setShowPrivacy(true)}>Privacy & Ads</button>
        </div>
      </motion.div>

      {IS_DEBUG && (
        <div className="debug-fab">
          <button className="fab-btn" title={showDebug ? 'Hide Word List' : 'Show Word List'} onClick={toggleDebug}>🔍</button>
          {false && (
            <button className="fab-btn" title={showGridDebug ? 'Hide Grid Debug' : 'Show Grid Debug'} onClick={toggleGridDebug}>📊</button>
          )}
        </div>
      )}

      {showConfirmNewGame && (
        <div className="confirm-overlay">
          <div className="confirm-modal">
            <h3>Start a new game?</h3>
            <p>Your current progress for this level will be lost.</p>
            <div className="confirm-actions">
              <button className="confirm-cancel" onClick={() => setShowConfirmNewGame(false)}>Cancel</button>
              <button className="confirm-accept" onClick={() => { setShowConfirmNewGame(false); doStartNewGame(); }}>Start New Game</button>
            </div>
          </div>
        </div>
      )}
      
      {showLevelSelect && (
        <LevelSelect
          currentLevel={gameState.level}
          onLevelSelect={handleLevelSelect}
          onClose={() => setShowLevelSelect(false)}
        />
      )}

      {showLevelComplete && (
      <LevelComplete
        level={completedLevel}
        score={gameState.score}
        wordsFound={gameState.foundWords.length}
        bonusPoints={completionBonus}
        tileClearingBonus={gameState.tileClearingBonus}
        allWordsFound={gameState.allWordsFound}
        onNextLevel={handleNextLevelFromComplete}
        onReplay={handleReplayLevel}
        onLevelSelect={handleLevelSelectFromComplete}
      />
    )}

      <Leaderboard 
        isOpen={showLeaderboard} 
        onClose={() => setShowLeaderboard(false)} 
      />

      <div className="ad-container ad-footer">
        <AdSlot />
      </div>
      <PrivacyModal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} />
      <CookieConsent onOpenPrivacy={() => setShowPrivacy(true)} />
      
      {IS_DEBUG && (
        <GridDebugView
          previousGrid={previousGrid}
          currentGrid={gameState.grid}
          isVisible={showGridDebug}
          onClose={toggleGridDebug}
        />
      )}
    </div>
  );
}

export default App;
